import { Font, StyleSheet as PdfStyleSheet } from '@react-pdf/renderer';

// Register Inter font from Google Fonts CDN
Font.register({
  family: 'Inter',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf', fontWeight: 500 },
    { src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf', fontWeight: 700 },
  ],
});

// Disable hyphenation
Font.registerHyphenationCallback(word => [word]);

export const COLORS = {
  primary: '#28BDAE',
  error: '#EA4335',
  warning: '#C57C00',
  positive: '#10B981',
  deeperInsight: '#9747FF',
  textPrimary: '#121723',
  textSecondary: '#606776',
  textTertiary: '#8B8FA3',
  textGrey: '#8F97A5',
  textDark: '#424B5A',
  border: '#E1E4E8',
  borderLight: '#F1F2F5',
  background: '#FAFBFC',
  white: '#FFFFFF',
  impactHigh: '#EF2A35',
  impactMed: '#F0B100',
  impactLow: '#22C55E',
  scoreGreen: '#22C14D',
  scoreYellow: '#FDC700',
  scoreRed: '#EF2A35',
};

export const CATEGORY_COLORS = {
  error: COLORS.error,
  suggestion: COLORS.warning,
  positive: COLORS.positive,
  'deeper-insights': COLORS.deeperInsight,
};

export const CATEGORY_LABELS = {
  error: 'Essential',
  suggestion: 'Caution',
  positive: 'Positive',
  'deeper-insights': 'New Idea',
};

export const styles = PdfStyleSheet.create({
  // Page
  page: {
    fontFamily: 'Inter',
    fontSize: 9,
    color: COLORS.textPrimary,
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    backgroundColor: COLORS.white,
  },

  // Header
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderLight,
  },
  headerDate: {
    fontSize: 8,
    color: COLORS.textTertiary,
  },

  // Score section
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  scoreMeta: {
    flexDirection: 'column',
  },
  scoreTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  scoreSubtitle: {
    fontSize: 9,
    color: COLORS.textSecondary,
  },

  // Overview section
  sectionContainer: {
    marginBottom: 20,
  },
  sectionHeading: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  sectionBody: {
    fontSize: 10,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
  },
  summaryText: {
    fontSize: 10,
    color: COLORS.textPrimary,
    lineHeight: 1.5,
    marginBottom: 12,
    fontWeight: 200,
  },

  // Table
  table: {
    marginBottom: 12,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 6,
    marginBottom: 6,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderLight,
  },
  colImpact: {
    width: '12%',
  },
  colIssue: {
    width: '38%',
    paddingRight: 8,
  },
  colImprove: {
    width: '50%',
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: 200,
    color: COLORS.textSecondary,
    textTransform: 'capitalize',
  },
  tableCellText: {
    fontSize: 11,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 500,
  },
  tableCellTextNormal: {
    fontSize: 10,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 400,
  },
  tableCellTextHeavy: {
    fontSize: 10,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 600,
  },
  impactBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 10,
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.white,
  },
  impactDot: {
    width: 4,
    height: 4,
    borderRadius: 3,
    marginRight: 3,
  },
  impactText: {
    fontSize: 10,
    fontWeight: 300,
    color: COLORS.textDark,
  },

  // Bullets
  bulletList: {
    marginTop: 4,
  },
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 10,
    fontSize: 9,
    color: COLORS.textSecondary,
  },
  bulletText: {
    flex: 1,
    fontSize: 10,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
  },

  // Context section
  contextGrid: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  contextItem: {
    flex: 1,
    minWidth: '45%',
    marginBottom: 12,
  },
  contextLabel: {
    fontSize: 8,
    fontWeight: 600,
    color: COLORS.textTertiary,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  contextValue: {
    fontSize: 8,
    color: COLORS.textSecondary,
    lineHeight: 1.5,
  },

  // Screen section
  screenHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderLight,
  },
  screenName: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  screenPageNum: {
    fontSize: 9,
    color: COLORS.textTertiary,
  },

  // Screen image with pins
  imageContainer: {
    position: 'relative',
    alignSelf: 'center',
    marginBottom: 16,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.borderLight,
    borderRadius: 4,
    overflow: 'hidden',
  },
  screenImage: {
    width: '100%',
    objectFit: 'contain',
  },
  pinOverlay: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 24,
    height: 18,
  },
  pinText: {
    fontSize: 7,
    fontWeight: 700,
    color: COLORS.white,
  },

  // Findings grid
  findingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  findingCardWrapper: {
    width: '49%',
    marginRight: '1%',
    marginBottom: 6,
  },

  // Finding card
  findingCard: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    padding: 10,
    backgroundColor: COLORS.white,
  },
  findingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderLight,
  },
  severityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  severityIcon: {
    fontSize: 8,
    color: COLORS.white,
    fontWeight: 700,
  },
  findingIndex: {
    fontSize: 8,
    color: COLORS.white,
    fontWeight: 600,
  },
  findingLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: COLORS.textTertiary,
    marginLeft: 6,
  },
  findingBody: {
    fontSize: 9,
    color: COLORS.textPrimary,
    lineHeight: 1.5,
    marginBottom: 6,
    fontWeight: 600,
  },
  findingHighlight: {
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  psychologySection: {
    marginTop: 6,
    paddingTop: 6,
  },
  psychologyPrinciple: {
    fontSize: 9,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 2,
  },
  psychologyInsight: {
    fontSize: 9,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
  },
  whySection: {
    marginTop: 6,
    paddingTop: 6,
  },
  whyLabel: {
    fontSize: 8,
    fontWeight: 600,
    color: COLORS.textTertiary,
    marginBottom: 2,
  },
  whyText: {
    fontSize: 9,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
  },

  // Deeper insight card
  deeperInsightCard: {
    borderWidth: 1,
    borderColor: COLORS.deeperInsight,
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
    backgroundColor: '#FDFAFF',
  },
  deeperInsightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  deeperInsightBadge: {
    backgroundColor: COLORS.deeperInsight,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  deeperInsightBadgeText: {
    fontSize: 8,
    fontWeight: 600,
    color: COLORS.white,
  },
  deeperInsightBody: {
    fontSize: 8,
    color: COLORS.textPrimary,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  recommendationLabel: {
    fontSize: 8,
    fontWeight: 600,
    color: COLORS.textTertiary,
    marginBottom: 2,
  },
  recommendationText: {
    fontSize: 9,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
    marginBottom: 8,
  },
  actionStep: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 4,
  },
  actionStepNumber: {
    width: 14,
    fontSize: 9,
    fontWeight: 600,
    color: COLORS.deeperInsight,
  },
  actionStepText: {
    fontSize: 9,
    color: COLORS.textPrimary,
    lineHeight: 1.4,
    fontWeight: 200,
    flex: 1,
  },
  metricsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 6,
  },
  metricTag: {
    fontSize: 7,
    color: COLORS.textTertiary,
    backgroundColor: COLORS.borderLight,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },

  // Footer
  pageFooter: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7,
    color: COLORS.textTertiary,
  },
});
